/**
 * Vulnerability Filter Configuration
 *
 * Filter dropdown widths and filter-specific settings.
 * NO JSX - pure TypeScript only.
 *
 * Pattern matches:
 * - src/sections/asset/config/filters.ts (filter widths)
 * - src/sections/seeds/config/filters.ts (filter widths)
 */

// ============================================================================
// FILTER DROPDOWN WIDTH CONFIGURATION
// ============================================================================

/**
 * Filter dropdown width configuration for vulnerability filters
 *
 * Defines custom widths for filters that need more space to display full text.
 * Filters not listed here will use the default width (400px).
 *
 * Available vulnerability filters:
 * - severity: Severity levels (Critical, High, Medium, Low, Info, Exposure)
 * - status: Status (Opened, Triaged, Remediated, Accepted, Deleted)
 * - source: Source integration or tool
 * - tags: User-defined tags
 * - attackSurface: Attack Surface categories
 * - kev: CISA KEV boolean
 * - exploit: Public exploit boolean
 * - sso: SSO identified boolean
 *
 * @example
 * ```tsx
 * const width = getFilterDropdownWidth('source'); // 500
 * const defaultWidth = getFilterDropdownWidth('severity'); // 400 (default)
 * ```
 */
export const FILTER_DROPDOWN_WIDTHS = {
  /** Compact dropdown for attack surface (External, Cloud) */
  attackSurface: 200,
  /** Compact dropdown for CISA KEV boolean (Yes, No) */
  kev: 200,
  /** Compact dropdown for exploit boolean (Yes, No) */
  exploit: 200,
  /** Compact dropdown for SSO boolean (Yes, No) */
  sso: 200,
  /** Compact dropdown for severity levels (Critical, High, Medium, Low, Info, Exposure) */
  severity: 250,
  /** Medium dropdown for status (Opened, Triaged, Remediated, Accepted, Deleted) */
  status: 250,
  /** Wide dropdown for source (integration names like "Nuclei", "Burp Suite Enterprise", etc.) */
  source: 450,
  /** Medium dropdown for tags (user-defined, variable length) */
  tags: 350,
  /** Date range filter - needs space for two date pickers */
  dateRange: 320,
  /** Default width for all other filters */
  default: 400,
} as const;

/**
 * Get dropdown width for a vulnerability filter field
 *
 * @param fieldName - The filter field name
 * @returns The configured width or default width
 *
 * @example
 * ```tsx
 * const width = getFilterDropdownWidth('source'); // 500
 * const width = getFilterDropdownWidth('unknownFilter'); // 400 (default)
 * ```
 */
export const getFilterDropdownWidth = (fieldName: string): number => {
  return (
    FILTER_DROPDOWN_WIDTHS[fieldName as keyof typeof FILTER_DROPDOWN_WIDTHS] ||
    FILTER_DROPDOWN_WIDTHS.default
  );
};
